<template>
  <section class="guided-mode__card guided-mode-symptoms">
    <p id="guided-symptoms-prompt" class="guided-mode__prompt">{{ $t('app.assessment_mode.guided.symptoms.prompt') }}</p>
    <div class="guided-mode-symptoms__tabs" role="tablist" aria-labelledby="guided-symptoms-prompt">
      <button
        v-for="tab in tabs"
        :id="`guided-symptoms-tab-${tab.id}`"
        :key="tab.id"
        ref="tabButtons"
        type="button"
        role="tab"
        class="guided-mode-symptoms__tab"
        :class="{ 'guided-mode-symptoms__tab--selected': activeTab === tab.id }"
        :aria-selected="String(activeTab === tab.id)"
        :aria-controls="`guided-symptoms-panel-${tab.id}`"
        :tabindex="activeTab === tab.id ? 0 : -1"
        @click="activeTab = tab.id"
        @keydown="moveBetweenTabs"
      >
        {{ $t(tab.labelToken) }}
      </button>
    </div>

    <div
      v-show="activeTab === 'search'"
      id="guided-symptoms-panel-search"
      class="guided-mode-symptoms__panel"
      role="tabpanel"
      aria-labelledby="guided-symptoms-tab-search"
    >
      <div ref="searchField" class="guided-mode-symptoms__search">
        <v-icon class="guided-mode-symptoms__search-icon" size="22">{{ mdiMagnify }}</v-icon>
        <!-- Not v-model: it waits for keyboard composition (Gboard) to end before updating. -->
        <input
          ref="searchInput"
          :value="query"
          class="guided-mode-symptoms__search-input"
          type="search"
          dir="auto"
          autocomplete="off"
          :aria-label="$t('app.assessment_mode.guided.symptoms.search_tab')"
          @input="query = $event.target.value"
          @focus="watchKeyboard"
          @blur="unwatchKeyboard"
        >
      </div>
      <option-input
        v-if="searchOptions.length"
        class="guided-mode-symptoms__options"
        :options="searchOptions"
        :value="selectedValues"
        multiple
        @select="selectSearchOption"
      />
      <guided-mode-symptom-chips
        v-if="activeTab === 'search'"
        heading-id="guided-symptoms-selected-search"
        :items="chips"
        @remove="removeChip"
      />
    </div>

    <div
      v-show="activeTab === 'list'"
      id="guided-symptoms-panel-list"
      class="guided-mode-symptoms__panel"
      role="tabpanel"
      aria-labelledby="guided-symptoms-tab-list"
    >
      <guided-mode-symptom-chips
        v-if="activeTab === 'list'"
        heading-id="guided-symptoms-selected-list"
        :items="chips"
        @remove="removeChip"
      />
      <option-input
        class="guided-mode-symptoms__options"
        :options="listOptions"
        :value="selectedValues"
        multiple
        @select="selectOption"
      />
    </div>

    <label class="guided-mode-symptoms__comprehensive">
      <input
        type="checkbox"
        class="guided-mode-symptoms__checkbox"
        :checked="comprehensiveSelected"
        @change="$emit('comprehensive-changed', $event.target.checked)"
      >
      <span class="guided-mode-symptoms__comprehensive-text">
        {{ $t('app.assessment_mode.guided.symptoms.comprehensive') }}
      </span>
    </label>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mdiMagnify } from '@mdi/js'
import { OptionInput } from '@/components/assessment-inputs'
import type { AssessmentInputOption } from '@/components/assessment-inputs'
import { normalizeSearchText } from '@/utils/searchText'
import GuidedModeSymptomChips from './GuidedModeSymptomChips.vue'
import type { GuidedModeSymptom } from '../types/guided-mode.types'
import { OTHER_SYMPTOM, searchSymptoms } from '../utils/symptoms'
import type { GuidedModeSymptomChip } from '../utils/symptoms'

type SymptomsTab = 'search' | 'list'

export default defineComponent({
  name: 'GuidedModeSymptoms',
  components: { GuidedModeSymptomChips, OptionInput },
  props: {
    symptoms: { type: Array as PropType<GuidedModeSymptom[]>, default: () => [] },
    selectedIds: { type: Array as PropType<string[]>, default: () => [] },
    otherSelected: { type: Boolean, default: false },
    comprehensiveSelected: { type: Boolean, default: false },
  },
  emits: ['toggle-symptom', 'other-changed', 'comprehensive-changed'],
  data: () => ({
    mdiMagnify,
    activeTab: 'search' as SymptomsTab,
    query: '',
    tabs: [
      { id: 'search' as SymptomsTab, labelToken: 'app.assessment_mode.guided.symptoms.search_tab' },
      { id: 'list' as SymptomsTab, labelToken: 'app.assessment_mode.guided.symptoms.list_tab' },
    ],
  }),
  computed: {
    sortedSymptoms(): GuidedModeSymptom[] {
      const collator = new Intl.Collator(this.$i18n.locale, { sensitivity: 'base' })
      return [...this.symptoms].sort((left, right) => (
        collator.compare(this.labelFor(left), this.labelFor(right))
      ))
    },
    otherOption(): AssessmentInputOption {
      const label = String(this.$t('app.assessment_mode.guided.symptoms.other'))
      return { value: OTHER_SYMPTOM, label }
    },
    listOptions(): AssessmentInputOption[] {
      return [
        ...this.sortedSymptoms.map(symptom => ({
          value: symptom.id,
          label: this.labelFor(symptom),
        })),
        this.otherOption,
      ]
    },
    searchOptions(): AssessmentInputOption[] {
      if (!normalizeSearchText(this.query)) return []
      const matches = searchSymptoms(this.sortedSymptoms, this.query, this.labelFor)
      return matches.length
        ? matches.map(symptom => ({ value: symptom.id, label: this.labelFor(symptom) }))
        : [this.otherOption]
    },
    selectedValues(): string[] {
      return this.otherSelected ? [...this.selectedIds, OTHER_SYMPTOM] : this.selectedIds
    },
    chips(): GuidedModeSymptomChip[] {
      const symptoms = this.selectedIds.flatMap(id => this.symptoms.filter(symptom => symptom.id === id))
        .map(symptom => ({ id: symptom.id, label: this.labelFor(symptom) }))
      return this.otherSelected
        ? [...symptoms, { id: OTHER_SYMPTOM, label: this.otherOption.label }]
        : symptoms
    },
  },
  beforeDestroy() {
    this.unwatchKeyboard()
  },
  methods: {
    labelFor(symptom: GuidedModeSymptom): string {
      return String(this.$t(symptom.labelToken))
    },
    selectOption(value: string) {
      if (value === OTHER_SYMPTOM) this.$emit('other-changed', !this.otherSelected)
      else this.$emit('toggle-symptom', value)
    },
    selectSearchOption(value: string) {
      this.selectOption(value)
      this.query = ''
      ;(this.$refs.searchInput as HTMLInputElement | undefined)?.focus()
    },
    removeChip(id: string) {
      if (id === OTHER_SYMPTOM) this.$emit('other-changed', false)
      else this.$emit('toggle-symptom', id)
    },
    moveBetweenTabs(event: KeyboardEvent) {
      const next: Partial<Record<string, SymptomsTab>> = {
        ArrowLeft: this.activeTab === 'search' ? 'list' : 'search',
        ArrowRight: this.activeTab === 'search' ? 'list' : 'search',
        Home: 'search',
        End: 'list',
      }
      const tab = next[event.key]
      if (!tab) return
      event.preventDefault()
      this.activeTab = tab
      this.$nextTick(() => {
        const buttons = (this.$refs.tabButtons || []) as HTMLButtonElement[]
        buttons[this.tabs.findIndex(item => item.id === tab)]?.focus()
      })
    },
    // Keeps the matches above an on-screen keyboard, which shrinks only the visual viewport.
    revealSearch() {
      const viewport = window.visualViewport
      if (!viewport || viewport.height >= window.innerHeight) return
      (this.$refs.searchField as HTMLElement | undefined)?.scrollIntoView?.({ block: 'start' })
    },
    watchKeyboard() {
      window.visualViewport?.addEventListener('resize', this.revealSearch)
      this.revealSearch()
    },
    unwatchKeyboard() {
      window.visualViewport?.removeEventListener('resize', this.revealSearch)
    },
  },
})
</script>
